import React, { useState, useEffect } from 'react';
import { MasterFlightRecord, formatFrequencyWithUnderscores, getDisplayFrequency, getDisplayDate, getFrequencyLabel, formatTime, getRouteString, formatDisplayDate } from '../../../utils/types';
import { CollapsiblePanel, InfoField, SectionHeader } from '../common';
import { findServiceTypeByCode, formatServiceTypeLabel } from '../../../utils/constants/serviceTypes';
import { useGetServiceTypesQuery } from '../../../services/redux/api';
import { useTimezone } from '../../../contexts/TimezoneContext';
import { utcToMYT } from '../../../utils/timeUtils';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { VariantsTable } from './VariantsTable';
import { VariantComparisonModal } from './VariantComparisonModal';

interface ExpandedRowDetailProps {
  isExpanded: boolean;
  record: MasterFlightRecord;
  variants?: MasterFlightRecord[]; // All variants of this flight number
  onEdit?: (record: MasterFlightRecord) => void;
  onDelete?: (id: string) => void;
  onCreateVariant?: (record: MasterFlightRecord) => void;
  onVariantSelect?: (variant: MasterFlightRecord) => void;
  onCollapse: () => void;
}

export function ExpandedRowDetail({
  isExpanded,
  record,
  variants = [],
  onEdit,
  onDelete,
  onCreateVariant,
  onVariantSelect,
  onCollapse,
}: ExpandedRowDetailProps) {
  const { data: serviceTypes = [] } = useGetServiceTypesQuery();
  const { timezone } = useTimezone();
  const [openSectors, setOpenSectors] = useState<boolean[]>(() => record.stations?.map(() => false) || []);

  // Display time based on timezone mode
  const displayTime = (utcTime: string) =>
    timezone === 'MYT' ? formatTime(utcToMYT(utcTime)) : formatTime(utcTime);
  const tzLabel = timezone === 'MYT' ? 'MYT' : 'UTC';

  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  // Default collapse all sectors when record/variant changes — also reset comparison
  useEffect(() => {
    const desiredLength = record.stations?.length ?? 0;
    setOpenSectors(Array.from({ length: desiredLength }, () => false));
    setIsCompareModalOpen(false);
  }, [record._id, record.itineraryVarId, record.stations?.length]);

  // ✅ Codeshares already embedded in record.stations[].codeshareFlightNo
  // No need to fetch - data comes from optimized API endpoint

  // Format service type for display
  const serviceType = findServiceTypeByCode(serviceTypes, record.iataServiceTypeCode);
  const serviceTypeDisplay = serviceType
    ? formatServiceTypeLabel(serviceType)
    : record.iataServiceTypeCode;

  const sectorsCount = record.stations?.length || 0;
  const anyOpen = openSectors.some(Boolean);

  const toggleSector = (idx: number) => {
    setOpenSectors(prev => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const expandAll = () => setOpenSectors(record.stations?.map(() => true) || []);
  const collapseAll = () => setOpenSectors(record.stations?.map(() => false) || []);

  return (
    <tr>
      <td colSpan={11} className={`p-0 bg-gray-50 expanded-row-detail ${isExpanded ? 'border-t-2 border-primary-500' : ''}`} style={{ borderTop: isExpanded ? undefined : 'none' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateRows: isExpanded ? '1fr' : '0fr',
            transition: 'grid-template-rows 400ms ease-in-out',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
          <div className="px-6 py-5 border-b border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <h3 className="m-0 text-2xl font-semibold text-gray-900 flex items-center gap-2">
                {record.flightNo}
                {record.isAdhoc && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-md text-xs font-bold uppercase">
                    Adhoc
                  </span>
                )}
              </h3>

              <span className="px-2.5 py-1 bg-primary-100 text-primary-500 rounded-md text-base font-semibold">
                {record.flightOwner}
              </span>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                <span className="font-mono text-base font-semibold text-gray-800 tracking-wider">
                  {formatFrequencyWithUnderscores(getDisplayFrequency(record.frequency, record.frequencyMYT, timezone))}
                </span>
                {record.frequency && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-md border bg-primary-50 text-primary-700 border-primary-500">
                    {getFrequencyLabel(record.frequency, record.freqRate)}
                  </span>
                )}
              </div>

              <span className="text-md text-gray-500">
                {getRouteString(record.stations)}
              </span>
            </div>

            <div className="flex gap-2.5">
              {onDelete && record._id && (
                <button
                  onClick={() => onDelete(record._id!)}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-danger-100 rounded-md text-danger-500 text-md font-medium cursor-pointer hover:opacity-95"
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                  Delete
                </button>
              )}

              {onEdit && (
                <button
                  onClick={() => onEdit(record)}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-primary-500 border-none rounded-md text-white text-md font-medium cursor-pointer hover:opacity-95"
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                  Edit Record
                </button>
              )}

              {onCreateVariant && (
                <button
                  onClick={() => onCreateVariant(record)}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-primary-500 rounded-md text-primary-500 text-md font-medium cursor-pointer hover:bg-primary-50"
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                  Create Itinerary
                </button>
              )}

              <button
                onClick={onCollapse}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-gray-200 rounded-md text-gray-600 text-md font-medium cursor-pointer hover:opacity-95"
              >
                Collapse
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* Flight Variations */}
          {variants.length > 0 && onVariantSelect && (
            <>
              <VariantsTable
                variants={variants}
                currentVariantId={record._id || ''}
                flightOwner={record.flightOwner}
                onVariantSelect={onVariantSelect}
                onCompareOpen={() => setIsCompareModalOpen(true)}
              />

              <VariantComparisonModal
                open={isCompareModalOpen}
                variants={variants}
                onClose={() => setIsCompareModalOpen(false)}
              />
            </>
          )}

          {/* Flight Info */}
          <div className="mb-6">
            <SectionHeader title="Flight Information" />

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-12 gap-x-5 gap-y-4">
                <div className="md:col-span-1">
                  <InfoField label="Season" value={record.seasonId} />
                </div>
                <div className="md:col-span-3">
                  <InfoField label="Service Type" value={serviceTypeDisplay} />
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs font-medium text-gray-400 mb-0.5 uppercase tracking-wide">Schedule Status</p>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide
                    ${record.scheduleStatus === 'ACTIVE'    ? 'bg-green-100 text-green-700'   :
                      record.scheduleStatus === 'CANCELLED' ? 'bg-red-100 text-red-600'       :
                      record.scheduleStatus === 'INACTIVE'  ? 'bg-gray-100 text-gray-600'     :
                      record.scheduleStatus === 'PENDING'   ? 'bg-amber-100 text-amber-700'   :
                      record.scheduleStatus === 'SUSPENDED' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-500'}`}>
                    {record.scheduleStatus || '—'}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <InfoField label="Start Date" value={formatDisplayDate(getDisplayDate(record.startDate, record.stations?.[0]?.std, timezone))} />
                </div>
                <div className="md:col-span-2">
                  <InfoField label="End Date" value={formatDisplayDate(getDisplayDate(record.endDate, record.stations?.[0]?.std, timezone))} />
                </div>
                <div className="md:col-span-1">
                  <InfoField label="Freq Rate" value={record.freqRate || 'Weekly'} />
                </div>
                <div className="md:col-span-1">
                  <InfoField label="Op. Suffix" value={record.operationalSuffix || '—'} />
                </div>
              </div>
            </div>
          </div>


          {/* Sectors */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <SectionHeader title={`Flight Sectors (${sectorsCount})`} />
              {sectorsCount > 0 && (
                <div className="flex items-center gap-2">
                  {anyOpen ? (
                    <button
                      type="button"
                      onClick={collapseAll}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-md text-gray-600 text-sm font-medium hover:bg-gray-50"
                    >
                      Collapse All
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={expandAll}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-md text-gray-600 text-sm font-medium hover:bg-gray-50"
                    >
                      Expand All
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-3 flex flex-col gap-2.5">
              {record.stations?.map((station, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleSector(idx)}
                    className="w-full px-4 py-2.5 flex items-center gap-4 cursor-pointer hover:bg-gray-50 text-left"
                    aria-expanded={openSectors[idx] ?? false}
                  >
                    <span className="px-2.5 py-1 bg-primary-500 text-white rounded text-xs font-bold tracking-wider">
                      SECTOR {idx + 1}
                    </span>

                    <div className="flex items-center gap-2.5 text-base font-semibold text-gray-900">
                      <span>{station.departureStation || '___'}</span>
                      {station.passengerTerminalDep && (
                        <span className="text-sm text-gray-400 font-normal">
                          T{station.passengerTerminalDep}
                        </span>
                      )}
                      <span className="text-primary-500">→</span>
                      <span>{station.arrivalStation || '___'}</span>
                      {station.passengerTerminalArr && (
                        <span className="text-sm text-gray-400 font-normal">
                          T{station.passengerTerminalArr}
                        </span>
                      )}
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                      <span className="font-mono text-sm text-gray-500">
                        {displayTime(station.std)} — {displayTime(station.sta)}
                        <span className="ml-1 text-xs text-gray-400">({tzLabel})</span>
                      </span>

                      {(station.ca ?? 0) > 0 && (
                        <span className="px-1.5 py-0 bg-warning-50 text-warning-700 rounded text-xs font-medium">
                          +{station.ca}d
                        </span>
                      )}

                      <span className={`text-gray-600 transition-transform duration-300 ease-in-out ${(openSectors[idx] ?? false) ? 'rotate-180' : 'rotate-0'}`}>
                        <ExpandMoreIcon sx={{ fontSize: 20 }} />
                      </span>
                    </div>
                  </button>

                  <CollapsiblePanel isOpen={openSectors[idx] ?? false} durationMs={300}>
                    <div className="px-4 py-4">
                      <div className="grid grid-cols-2 md:grid-cols-10 gap-3">
                        <InfoField label={`STD (${tzLabel})`} value={displayTime(station.std)} mono small />
                        <InfoField label={`STA (${tzLabel})`} value={displayTime(station.sta)} mono small />
                        {timezone === 'UTC' && (
                          <>
                            <InfoField label="UTC Dep" value={station.utcLocalTimeVariationDep || '—'} mono small />
                            <InfoField label="UTC Arr" value={station.utcLocalTimeVariationArr || '—'} mono small />
                          </>
                        )}
                        <InfoField label="Day Δ" value={`${station.cd ?? 0} / ${station.ca ?? 0}`} small />
                        <InfoField label="Aircraft" value={station.iataSubTypeCode || '—'} small />
                        <InfoField label="Config" value={station.aircraftConfiguration || '—'} mono small />
                        <InfoField label="Owner" value={station.aircraftOwner || '—'} small />
                        <InfoField label="Tail No" value={station.tailNo || '—'} mono small />
                        <InfoField label="Onward" value={station.onwardFlight || '—'} mono small />
                      </div>

                      {/* Codeshare Information for this Sector */}
                      {station.codeshareFlightNo && station.codeshareFlightNo.length > 0 && (
                        <div className="pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <LinkIcon sx={{ fontSize: 16 }} className="text-blue-600" />
                            <span className="text-sm font-semibold text-gray-700">
                              Codeshare Flights ({station.codeshareFlightNo.length})
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {station.codeshareFlightNo.map((flight, csIdx) => (
                              <span
                                key={csIdx}
                                className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-mono font-medium"
                              >
                                {flight}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsiblePanel>
                </div>
              ))}
            </div>
          </div>
          </div>
          </div>
        </div>
      </td>
    </tr>
  );
}
